import { datadogRum } from "@datadog/browser-rum";

import { environmentConfig } from "./environment-config.ts";
import type { OneTrustWindow } from "./onetrust-types.ts";

const isExternalDeployment =
	(import.meta.env["VITE_DEPLOYMENT_TYPE"] as string)?.trim() === "external";

const ONETRUST_ANALYTICS_CATEGORY_ID = "C0002";

/**
 * Reads OneTrust's active consent groups (populated once the user's consent
 * choice is resolved, either from a cached cookie or a fresh banner
 * interaction) and determines whether the Performance/Analytics category is
 * active. Only ever meaningful for external deployments — OneTrust is
 * stripped entirely from internal builds.
 */
function getOneTrustAnalyticsConsent(): boolean {
	const activeGroups = (globalThis as OneTrustWindow).OnetrustActiveGroups;
	if (!activeGroups) return false;

	return activeGroups
		.split(",")
		.map((group) => group.trim())
		.includes(ONETRUST_ANALYTICS_CATEGORY_ID);
}

/**
 * Syncs Datadog RUM's tracking consent with OneTrust's current
 * Performance/Analytics consent state. Should be invoked whenever OneTrust
 * resolves or updates the user's consent choice.
 */
function syncDatadogTrackingConsent(): void {
	datadogRum.setTrackingConsent(
		getOneTrustAnalyticsConsent() ? "granted" : "not-granted",
	);
}

/**
 * Wraps the OneTrust `OptanonWrapper` global (declared as a no-op in
 * `index.html`, invoked by OneTrust once on initial consent resolution and
 * again on every subsequent consent change) so Datadog RUM's tracking
 * consent stays in sync, without clobbering any other behavior that may be
 * attached to it.
 */
function wireDatadogConsentToOneTrust(): void {
	const oneTrustWindow = globalThis as OneTrustWindow;
	const existingWrapper = oneTrustWindow.OptanonWrapper;

	oneTrustWindow.OptanonWrapper = () => {
		existingWrapper?.();
		syncDatadogTrackingConsent();
	};

	// Sync immediately in case OneTrust has already resolved consent (e.g. a
	// returning visitor with a cached consent cookie) before this runs.
	syncDatadogTrackingConsent();
}

/**
 * Initializes Datadog Real User Monitoring (RUM) for the application.
 * Should be called once during application startup.
 */
export function initializeMonitoring(): void {
	datadogRum.init({
		applicationId: "674487b8-550a-4d0c-aa1a-b689434ee86c",
		clientToken: "pubff0bf8154e6c369bd2c158524cd1031b",
		site: "datadoghq.com",
		service: "tql.pricing.ltlquoting.api",
		env: environmentConfig.environmentName,

		// Specify a version number to identify the deployed version of your application in Datadog
		version: "1.0.0",
		sessionSampleRate: 50,
		sessionReplaySampleRate: 0,
		defaultPrivacyLevel: "mask-user-input",
		trackUserInteractions: true,
		trackResources: true,
		trackLongTasks: true,

		// Block RUM from tracking until OneTrust resolves consent for external apps (GDPR/CCPA).
		...(isExternalDeployment && { trackingConsent: "not-granted" }),

		// Enable trace correlation with backend logs
		allowedTracingUrls: [
			{
				match: /https:\/\/.*\.pricing\.apps\.tql\.com/,
				propagatorTypes: ["datadog", "tracecontext"],
			},
			{
				match: /https:\/\/.*\.tqlapp\.com/,
				propagatorTypes: ["datadog", "tracecontext"],
			},
		],
		traceSampleRate: 20,
	});

	if (isExternalDeployment) {
		wireDatadogConsentToOneTrust();
	}

	// Start tracking manually since we're not using the React plugin
	datadogRum.startSessionReplayRecording();
}

/**
 * Sets the user identity on DataDog RUM sessions.
 * Send userId, type, and name. Name is the internal/employee's name or the customer's name for external users
 */
export function setMonitoringUser(
	userId: string,
	userType: string,
	name?: string,
): void {
	datadogRum.setUser({
		id: userId,
		type: userType,
		name: name,
	});
}

/**
 * Clears the user identity from DataDog RUM.
 * Should be called on logout.
 */
export function clearMonitoringUser(): void {
	datadogRum.clearUser();
}
